/**
 * The sentence under each screen's title. The titles live in `navigatie`, so a page and its
 * menu entry cannot disagree about what the page is called.
 */
export const paginas = {
  overzicht: {
    uitleg: "Beheren van alles wat de wedstrijdleiders regelen, op één plek.",
    geenSeizoen: "Nog geen seizoen ingericht.",
    aandachtspunten: "Aandachtspunten",
    geenAandachtspunten: "Er zijn nu geen aandachtspunten.",
    /** i18next pluralises these, so a language with more than two plural forms needs no call site. */
    regels: {
      ploegen_one: "{{count}} ploeg ingeschreven",
      ploegen_other: "{{count}} ploegen ingeschreven",
      ondersteuners_one: "{{count}} ondersteuner heeft doorgegeven",
      ondersteuners_other: "{{count}} ondersteuners hebben doorgegeven",
      seizoen: "Seizoen {{naam}} loopt",
      wedstrijddagen_one: "{{count}} wedstrijddag",
      wedstrijddagen_other: "{{count}} wedstrijddagen",
      inschrijfperiodes_one: "{{count}} periode, {{open}} open",
      inschrijfperiodes_other: "{{count}} periodes, {{open}} open",
      indelingen_one: "{{count}} ploeg ingedeeld",
      indelingen_other: "{{count}} ploegen ingedeeld",
      stamgegevens: "5 lijsten",
      bevoegdheden_one: "{{count}} persoon bevoegd",
      bevoegdheden_other: "{{count}} personen bevoegd",
    },
  },

  seizoenen: {
    uitleg:
      "Een seizoen is een naam en twee datums. Niveaus, klassen en functies gelden voor elk seizoen. Een nieuw seizoen begint verder leeg: voer de wedstrijddagen en inschrijfperiodes opnieuw in.",
  },

  inschrijfperiodes: {
    uitleg:
      "Buiten de inschrijfperiode kan niemand inschrijven, iets wijzigen of zich terugtrekken. Alleen een wedstrijdleider kan dat nog, namens een korps of een persoon.",
  },

  wedstrijdboekjes: {
    uitleg:
      "Je vinkt per boekje de wedstrijddagen aan die erin komen. Elke versie die je maakt, staat achterin het boekje bij de documentversies.",
  },

  wedstrijdboekjesSjabloon: {
    uitleg:
      "Het Word-document waarmee de Wedstrijdplanner elk nieuw wedstrijdboekje vult. Upload je een nieuw sjabloon? Dan geldt het vanaf de volgende versie die je maakt.",
  },

  reistijden: {
    /** Both groups see a rounded schatting of their own reistijd on their own form (ADR-0053). */
    uitleg:
      "Hoe lang een korps en een ondersteuner naar elke wedstrijddag rijden. De planning zet de starttijden op volgorde van reistijd. Bij ondersteuners helpt de reistijd ook kiezen wie er op een volle dag dienst doet.",
    /** The landing page's two cards say a little more than the sidebar's one-liner does. */
    kaarten: {
      korpsen:
        "Vanaf het adres van elk korps. De reistijd bepaalt de terugvaldag en de volgorde van de starttijden. Je kunt elke reistijd met de hand overschrijven.",
      ondersteuners:
        "Vanaf de eigen postcode van elke ondersteuner. De reistijd beslist als functierotatie geen verschil maakt. Je ziet hier ook van wie we geen postcode hebben.",
    },
    tellingen: {
      korpsenZonderPunt_one: "1 korps zonder coördinaten",
      korpsenZonderPunt_other: "{{count}} korpsen zonder coördinaten",
      korpsenCompleet: "Elk korps heeft coördinaten",
      ondersteunersZonderPunt_one: "1 ondersteuner zonder postcode",
      ondersteunersZonderPunt_other: "{{count}} ondersteuners zonder postcode",
      ondersteunersCompleet: "Elke ondersteuner heeft een postcode",
    },
  },

  reistijdenKorpsen: {
    uitleg:
      "Hoe lang elk korps naar elke wedstrijddag rijdt. De reistijd bepaalt de volgorde waarin de planning starttijden uitdeelt. Een korps ziet hiervan alleen een afgeronde schatting op de inschrijving.",
  },

  reistijdenOndersteuners: {
    uitleg:
      "Hoe lang elke ondersteuner naar elke wedstrijddag rijdt, vanaf de eigen postcode. De planning weegt de reistijd lichter dan functierotatie. De reistijd kiest nooit iemands functie. De reistijd bepaalt alleen wie er dienst doet op een dag waarvoor meer mensen zich opgeven dan er plekken zijn.",
    tabel: {
      label: "Ondersteuners",
      zoek: "Zoek een ondersteuner",
      leeg: "Er zijn nog geen ondersteuners.",
      kies: "Kies een wedstrijddag om te zien hoe lang elke ondersteuner ernaartoe rijdt.",
      naam: "Ondersteuner",
      reistijd: "Reistijd",
      nietBerekend: "Nog niet berekend",
      /**
       * Never a zero or a dash, which read as *nearby* (ADR-0053), and one wording per gap: an
       * unmapped postcode is not a missing one. The banner above carries the full sentence.
       */
      GeenPostcode: "Geen postcode",
      GeenBronrecord: "Geen postcode",
      NietGeocodeerd: "Postcode niet gevonden",
      nietBerekendCount_one: "1 reistijd is nog niet berekend.",
      nietBerekendCount_other: "{{count}} reistijden zijn nog niet berekend.",
      /**
       * The filter's own labels: GeenPostcode and GeenBronrecord read apart here, unlike the two
       * rows above - a beheerder ticking boxes can't tell two both saying "Geen postcode" apart.
       */
      filter: {
        label: "Postcode",
        Gevonden: "Gevonden",
        GeenPostcode: "Geen postcode in VBS of het JBNL Portaal",
        GeenBronrecord: "Staat niet in VBS of het JBNL Portaal",
        NietGeocodeerd: "Postcode niet gevonden",
      },
    },
    /**
     * One sentence per reason, not per person: a fresh seizoen can list a hundred names, and the
     * reason is the distinction that matters because each sends a beheerder somewhere else.
     */
    zonderPunt: {
      titel_one: "Van 1 ondersteuner kunnen we geen reistijd berekenen",
      titel_other:
        "Van {{count}} ondersteuners kunnen we geen reistijd berekenen",
      uitleg:
        "De planning telt hun reistijd niet mee en kijkt alleen naar functierotatie. Zo hebben ze geen nadeel. Vul de postcode aan om hun reistijd wel mee te tellen.",
      GeenPostcode_one: "1 ondersteuner heeft geen postcode in VBS of het JBNL Portaal:",
      GeenPostcode_other:
        "{{count}} ondersteuners hebben geen postcode in VBS of het JBNL Portaal:",
      GeenBronrecord_one:
        "1 ondersteuner staat niet in VBS en niet in het JBNL Portaal. Deze persoon is in de Wedstrijdplanner aangemaakt. Je kunt in VBS of het JBNL Portaal dus niets verbeteren:",
      GeenBronrecord_other:
        "{{count}} ondersteuners staan niet in VBS en niet in het JBNL Portaal. Deze personen zijn in de Wedstrijdplanner aangemaakt. Je kunt in VBS of het JBNL Portaal dus niets verbeteren:",
      /** Covers two states the row cannot tell apart: a postcode PDOK refuses, and one the
       * nightly run has not reached yet. So it asks for a check without claiming which. */
      NietGeocodeerd_one:
        "Van 1 ondersteuner staat de postcode nog niet op de kaart. Controleer of de postcode klopt. Klopt de postcode? Dan zoekt de Wedstrijdplanner die vannacht vanzelf op:",
      NietGeocodeerd_other:
        "Van {{count}} ondersteuners staat de postcode nog niet op de kaart. Controleer of de postcodes kloppen. Kloppen ze? Dan zoekt de Wedstrijdplanner ze vannacht vanzelf op:",
      /** The postcode belongs in the name for the group that is about a postcode. */
      naamMetPostcode: "{{naam}} ({{postcode}})",
      naamMetVbsNummer: "{{naam}} (<a>VBS {{nummer}}<icon/></a>)",
      naamMetPostcodeEnVbsNummer:
        "{{naam}} ({{postcode}}, <a>VBS {{nummer}}<icon/></a>)",
    },
  },

  ploegen: {
    titel: "Ploegen per korps",
    terug: "Alle korpsen",
    kies: "Kies een korps om de ploegen te zien.",
  },

  ondersteuners: {
    geenPeriode:
      "Dit seizoen heeft nog geen inschrijfperiode voor ondersteuners. Maak eerst een inschrijfperiode aan.",

    /** Named per reason: a missing address and a shared one are different jobs. */
    vbsWaarschuwing: {
      titel_one: "1 ondersteuner is niet uit VBS overgenomen",
      titel_other: "{{count}} ondersteuners zijn niet uit VBS overgenomen",
      uitleg:
        "Deze personen staan wel in VBS, maar de Wedstrijdplanner kon ze niet overnemen. " +
        "Pas hun gegevens aan in VBS. Na de volgende synchronisatie verdwijnt deze melding vanzelf.",
      ontbrekendEmail: "{{naam}} ({{soort}}) heeft geen e-mailadres in VBS.",
      dubbelEmail:
        "{{naam}} ({{soort}}) deelt het e-mailadres {{email}} met een andere contactpersoon. " +
        "Zolang dat zo is, gebruikt de Wedstrijdplanner dit adres niet.",
      vbsNummer: "VBS {{nummer}}",
    },
    terug: "Zoek een andere ondersteuner",
    kies: "Zoek een ondersteuner om de doorgegeven beschikbaarheid te zien.",
    filterReedsIngeschreven: "Al ingeschreven",
    filterReedsIngeschrevenVerbergen: "Verbergen",
    filterReedsIngeschrevenAlleen: "Alleen ingeschreven tonen",
    /** The detail card's title where the naam is not at hand - it names what is being edited. */
    detailTitel: "Beschikbaarheid",
    /** Namens handelen stays visible while it happens: the row records both people. */
    namens:
      "Je handelt <strong>namens deze ondersteuner</strong>. Alles wat je hier wijzigt, wordt vastgelegd op jouw naam, namens deze persoon.",
    namensGesloten:
      " De inschrijfperiode is gesloten. Omdat je namens iemand anders handelt, kun je hier alsnog wijzigen. Voor je <em>eigen</em> beschikbaarheid geldt de sluitingsdatum wel.",
  },

  lokaleAccounts: {
    uitleg:
      "Iedereen die op de aanmeldsite kan inloggen, met welke inlogmethode dan ook. Een inloglink werkt bij elke inlogmethode hetzelfde: de link gaat naar het adres dat JBNL van iemand heeft. Een account zonder gekoppelde persoon staat onder Wacht op koppeling.",
  },

  bevoegdheden: {
    uitleg:
      "Wie welke functie mag vervullen. Een bevoegdheid blijft gelden na het einde van een seizoen.",
  },

  stamgegevens: {
    uitleg:
      "Deze lijsten gelden voor alle seizoenen. Elk nieuw seizoen gebruikt dezelfde lijsten.",
    /** The landing page's five cards say a little more than the sidebar's one-liners do. */
    kaarten: {
      functies:
        "Wat een ondersteuner op een baan of een wedstrijddag doet, en bij welke klasse en welk niveau dat nodig is.",
      klassen:
        "Waarin een ploeg uitkomt, en voor welke leeftijdsgroepen de klasse openstaat.",
      niveaus:
        "Het stadium van de competitie waarin een wedstrijddag valt. Lokale wedstrijden komen hier niet voor.",
      speelschemas:
        "Met welke tijden een nieuwe wedstrijdbaan begint, per klasse en niveau.",
      standaardteksten:
        "Kant-en-klare tekst voor Briefing, Meldtijd en Opmerkingen bij het invullen van een wedstrijddag.",
    },
    tellingen: {
      functies_one: "{{count}} functie",
      functies_other: "{{count}} functies",
      klassen_one: "{{count}} klasse",
      klassen_other: "{{count}} klassen",
      niveaus_one: "{{count}} niveau",
      niveaus_other: "{{count}} niveaus",
      speelschemas_one: "{{count}} schema",
      speelschemas_other: "{{count}} schema's",
      standaardteksten_one: "{{count}} standaardtekst",
      standaardteksten_other: "{{count}} standaardteksten",
    },
  },

  functies: {
    uitleg:
      "Wat een ondersteuner op een baan of een wedstrijddag doet, en bij welke klasse en welk niveau dat nodig is.",
  },
  klassen: {
    uitleg:
      "Waarin een ploeg uitkomt, en voor welke leeftijdsgroepen de klasse openstaat.",
  },
  niveaus: {
    uitleg:
      "Het stadium van de competitie waarin een wedstrijddag valt. De regio's organiseren de lokale wedstrijden zelf. Die komen hier niet voor.",
  },
  speelschemas: {
    uitleg:
      "Met welke tijden een nieuwe wedstrijdbaan begint, per klasse en niveau. Bestaande wedstrijdbanen veranderen niet mee.",
  },
  synchronisaties: {
    uitleg:
      "De Wedstrijdplanner leest het JBNL Portaal en VBS elke nacht. Hier zie je wanneer dat voor het laatst gebeurde en start je zelf een synchronisatie.",
  },
  standaardteksten: {
    uitleg:
      "Kant-en-klare tekst voor Briefing, Meldtijd en Opmerkingen. Bij het invullen van een wedstrijddag kies je hieruit, of typ je zelf iets anders.",
  },
  handleiding: {
    uitleg:
      "Stap voor stap uitgelegd hoe je als beheerder de backoffice gebruikt.",
  },
} as const;
